import { escapeHtml } from './helpers';
import { escapeHtmlAttr, resolveEmailImageUrl } from './emailAssetUrl';
import {
  accentLinePaddingWhenHidden,
  HERO_PRESET_IMAGES,
  normalizeHeroButtonRadius,
  resolveAccentLineOptions,
  resolveHeroContentStyles,
  type HeroContentTemplate,
} from './heroContentUtils';

type Content = Record<string, unknown>;
type Styles = Record<string, unknown>;

const LEGACY_HERO_IMAGE_URLS: Record<string, string> = {
  'https://images.unsplash.com/photo-1522202222206-72cc1c482f5a?auto=format&fit=crop&w=1200&q=80':
    HERO_PRESET_IMAGES.split,
  'https://images.unsplash.com/photo-1522202222206-72cc1c482f5a': HERO_PRESET_IMAGES.split,
};

function resolveHeroEmailImage(raw: string, assetOrigin?: string): string {
  const trimmed = raw.trim();
  const migrated = LEGACY_HERO_IMAGE_URLS[trimmed] || trimmed;
  return resolveEmailImageUrl(migrated, assetOrigin);
}

const buttonRadiusCss = (
  content: Content,
  r: ReturnType<typeof resolveHeroContentStyles>
) => normalizeHeroButtonRadius(content.buttonBorderRadius, r.buttonRadius);

const renderButtonLink = (
  content: Content,
  r: ReturnType<typeof resolveHeroContentStyles>,
  padding = '12px 24px',
  lightOutline = false
): string => {
  const radius = buttonRadiusCss(content, r);
  const isOutline = content.buttonStyle === 'outline';
  const isGhost = content.buttonStyle === 'ghost';
  const borderColor = lightOutline ? '#FFFFFF' : r.buttonBg;
  const bg = isOutline || isGhost ? 'transparent' : r.buttonBg;
  const color = lightOutline
    ? '#FFFFFF'
    : isOutline || isGhost
      ? r.buttonBg
      : r.buttonColor;
  const border = isOutline ? `border: 1px solid ${borderColor};` : '';
  const decoration = isGhost ? 'text-decoration: underline;' : 'text-decoration: none;';
  return `<a href="${escapeHtml(String(content.buttonUrl || '#'))}" class="hero-content-button" style="display: inline-block; background-color: ${bg}; color: ${color}; ${border} padding: ${escapeHtml(String(content.buttonPadding || padding))}; border-radius: ${radius} !important; font-size: ${r.buttonFontSize}; font-weight: 600; ${decoration} font-family: ${r.fontFamily};">${escapeHtml(String(content.buttonText))}</a>`;
};

const renderButton = (
  content: Content,
  r: ReturnType<typeof resolveHeroContentStyles>,
  align: string,
  lightOutline = false
): string => {
  if (!content.buttonText || content.showButton === false) return '';
  return `<div class="hero-content-button-wrap" style="text-align: ${align};">${renderButtonLink(content, r, '12px 24px', lightOutline)}</div>`;
};

const renderEyebrow = (
  content: Content,
  r: ReturnType<typeof resolveHeroContentStyles>,
  align: string,
  light = false
): string => {
  if (!content.subtitle) return '';
  const color =
    (content.eyebrowColor as string) || (light ? 'rgba(255,255,255,0.75)' : r.textColor);
  return `<p class="hero-content-eyebrow" style="margin: 0 0 10px 0; font-size: 11px; font-weight: 600; letter-spacing: 0.14em; text-transform: uppercase; color: ${color}; font-family: ${r.fontFamily}; text-align: ${align}; line-height: 1.2;">${escapeHtml(String(content.subtitle))}</p>`;
};

export function renderHeroContentExtendedHtml(
  template: Extract<
    HeroContentTemplate,
    'centered-light' | 'banner-image' | 'accent-line' | 'overlay-minimal'
  >,
  content: Content,
  styles: Styles,
  fontFamily: string,
  isMobile = false,
  assetOrigin?: string
): string {
  const r = resolveHeroContentStyles(template, content, styles, fontFamily, isMobile);
  const wrapPad = styles.padding || '0';
  const img = content.image ? resolveHeroEmailImage(String(content.image), assetOrigin) : '';
  const accentColor = (content.accentColor as string) || '#111827';

  if (template === 'centered-light') {
    const eyebrow = renderEyebrow(content, r, 'center');
    const heading = content.heading
      ? `<h1 class="hero-content-heading" style="margin: 0 0 12px 0; font-size: ${r.headingSize}; font-weight: ${r.headingWeight}; color: ${r.headingColor}; font-family: ${r.fontFamily}; text-align: center; line-height: 1.2;">${escapeHtml(String(content.heading))}</h1>`
      : '';
    const text = content.text
      ? `<p class="hero-content-text" style="margin: 0 auto; max-width: 480px; font-size: ${r.textSize}; color: ${r.textColor}; line-height: 150%; font-family: ${r.fontFamily}; text-align: center;">${escapeHtml(String(content.text))}</p>`
      : '';
    const button =
      content.buttonText && content.showButton !== false
        ? renderButton(content, r, 'center')
        : '';
    return `<div class="hero-content-wrap hero-content-centered-light" style="padding: ${wrapPad}; background-color: ${r.containerBg}; font-family: ${r.fontFamily}; width: 100%; box-sizing: border-box; text-align: center;">
      <div style="padding: ${r.headingPadding}; background-color: ${r.contentBg};">${eyebrow}${heading}</div>
      ${text ? `<div style="padding: ${r.textPadding}; background-color: ${r.contentBg};">${text}</div>` : ''}
      ${button ? `<div style="padding: ${r.buttonSectionPadding}; background-color: ${r.contentBg};">${button}</div>` : ''}
    </div>`;
  }

  if (template === 'banner-image') {
    const bannerSrc = img || resolveHeroEmailImage(HERO_PRESET_IMAGES.banner, assetOrigin);
    const imageHtml = `<div style="overflow: hidden; max-height: ${r.bannerImageMaxHeight}px; background-color: ${r.splitImageBg};"><img src="${escapeHtmlAttr(bannerSrc)}" alt="Hero" class="hero-content-image" style="width: 100%; height: ${r.bannerImageMaxHeight}px; object-fit: cover; object-position: center; display: block; border: 0;" /></div>`;
    const heading = content.heading
      ? `<div style="padding: ${r.headingPadding}; background-color: ${r.contentBg}; text-align: center;"><h1 class="hero-content-heading" style="margin: 0 0 10px 0; font-size: ${r.headingSize}; font-weight: ${r.headingWeight}; color: ${r.headingColor}; font-family: ${r.fontFamily}; line-height: 1.2;">${escapeHtml(String(content.heading))}</h1></div>`
      : '';
    const text = content.text
      ? `<div style="padding: ${r.textPadding}; background-color: ${r.contentBg}; text-align: center;"><p class="hero-content-text" style="margin: 0 auto; max-width: 440px; font-size: ${r.textSize}; color: ${r.textColor}; line-height: 150%; font-family: ${r.fontFamily};">${escapeHtml(String(content.text))}</p></div>`
      : '';
    const button =
      content.buttonText && content.showButton !== false
        ? `<div style="padding: ${r.buttonSectionPadding}; background-color: ${r.contentBg};">${renderButton(content, r, 'center')}</div>`
        : '';
    return `<div class="hero-content-wrap hero-content-banner-image" style="padding: ${wrapPad}; background-color: ${r.containerBg}; font-family: ${r.fontFamily}; width: 100%; box-sizing: border-box;">${imageHtml}${heading}${text}${button}</div>`;
  }

  if (template === 'accent-line') {
    const accent = resolveAccentLineOptions(content, false);
    const headingPad = accent.show
      ? r.headingPadding
      : accentLinePaddingWhenHidden(r.headingPadding);
    const textPad = accent.show ? r.textPadding : accentLinePaddingWhenHidden(r.textPadding);
    const buttonPad = accent.show
      ? r.buttonSectionPadding
      : accentLinePaddingWhenHidden(r.buttonSectionPadding);
    const eyebrow = renderEyebrow(content, r, 'left');
    const heading = content.heading
      ? `<h1 class="hero-content-heading" style="margin: 0 0 10px 0; font-size: ${r.headingSize}; font-weight: ${r.headingWeight}; color: ${r.headingColor}; font-family: ${r.fontFamily}; line-height: 1.2; text-align: left;">${escapeHtml(String(content.heading))}</h1>`
      : '';
    const text = content.text
      ? `<p class="hero-content-text" style="margin: 0; font-size: ${r.textSize}; color: ${r.textColor}; line-height: 150%; font-family: ${r.fontFamily}; text-align: left;">${escapeHtml(String(content.text))}</p>`
      : '';
    const button =
      content.buttonText && content.showButton !== false
        ? renderButton(content, r, 'left')
        : '';
    return `<div class="hero-content-wrap hero-content-accent-line" style="padding: ${wrapPad}; background-color: ${r.containerBg}; font-family: ${r.fontFamily}; width: 100%; box-sizing: border-box;">
      <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%">
        <tr>
          ${accent.show ? `<td class="hero-accent-line-bar" style="width: ${accent.widthPx}px; background-color: ${accentColor}; font-size: 0; line-height: 0;">&nbsp;</td>` : ''}
          <td style="vertical-align: top;">
            <div style="padding: ${headingPad}; background-color: ${r.contentBg};">${eyebrow}${heading}</div>
            ${text ? `<div style="padding: ${textPad}; background-color: ${r.contentBg};">${text}</div>` : ''}
            ${button ? `<div style="padding: ${buttonPad}; background-color: ${r.contentBg};">${button}</div>` : ''}
          </td>
        </tr>
      </table>
    </div>`;
  }

  const overlaySrc = img || resolveHeroEmailImage(HERO_PRESET_IMAGES.overlay, assetOrigin);
  const overlayH = r.overlayMinHeight;
  const eyebrow = renderEyebrow(content, r, 'left', true);
  const heading = content.heading
    ? `<h1 class="hero-content-heading" style="margin: 0 0 8px 0; font-size: ${r.headingSize}; font-weight: ${r.headingWeight}; color: ${r.headingColor}; font-family: ${r.fontFamily}; line-height: 1.15; text-align: left;">${escapeHtml(String(content.heading))}</h1>`
    : '';
  const text = content.text
    ? `<p class="hero-content-text" style="margin: 0 0 16px 0; max-width: 420px; font-size: ${r.textSize}; color: ${r.textColor}; line-height: 150%; font-family: ${r.fontFamily}; text-align: left;">${escapeHtml(String(content.text))}</p>`
    : '';
  const button =
    content.buttonText && content.showButton !== false
      ? renderButton(content, r, 'left', true)
      : '';
  const innerPad = isMobile ? '28px 20px' : '40px 32px';
  const overlayBg = `background-image: linear-gradient(to top, rgba(17,24,39,0.92) 0%, rgba(17,24,39,0.45) 50%, rgba(17,24,39,0.1) 100%), url('${escapeHtmlAttr(overlaySrc)}'); background-size: cover; background-position: center center; background-color: #111827; background-repeat: no-repeat;`;

  return `<div class="hero-content-wrap hero-content-overlay-minimal" style="padding: ${wrapPad}; background-color: ${r.containerBg}; font-family: ${r.fontFamily}; width: 100%; box-sizing: border-box;">
    <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="width: 100%;">
      <tr>
        <td class="hero-overlay-cell" style="${overlayBg} min-height: ${overlayH}px; height: ${overlayH}px; vertical-align: bottom; padding: ${innerPad};">
          ${eyebrow}${heading}${text}${button}
        </td>
      </tr>
    </table>
  </div>`;
}
